import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import SideMenu from '../components/SideMenu';
import { Plus, Trash2, Menu, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const AdminSignatureNames = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [signatureNames, setSignatureNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    const effectiveRole = user.effective_role || user.role;
  if (effectiveRole !== 'Admin' && effectiveRole !== 'Manager') {
      toast.error('Only Admin/Manager can access this page');
      navigate('/');
      return;
    }
    loadSignatureNames();
  }, [user, navigate]);

  const loadSignatureNames = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/masters/signature-names');
      setSignatureNames(res.data);
    } catch (err) {
      console.error('Error loading signature names:', err);
      toast.error('Failed to load signature names');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newName.trim()) {
      toast.error('Please enter a name');
      return;
    }
    
    try {
      setIsAdding(true);
      await axiosInstance.post('/masters/signature-names', { name: newName.trim() });
      toast.success('Signature name added successfully');
      setNewName('');
      loadSignatureNames();
    } catch (err) {
      console.error('Error adding signature name:', err);
      toast.error('Failed to add signature name');
    } finally {
      setIsAdding(false);
    }
  };

  const handleDelete = async (nameId) => {
    if (!window.confirm('Are you sure you want to delete this signature name?')) return;
    
    try {
      await axiosInstance.delete(`/masters/signature-names/${nameId}`);
      toast.success('Signature name deleted successfully');
      loadSignatureNames();
    } catch (err) {
      console.error('Error deleting signature name:', err);
      toast.error('Failed to delete signature name');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <SideMenu user={user} isOpen={menuOpen} onClose={() => setMenuOpen(false)} onLogout={onLogout} />
      
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 hover:bg-amber-100 rounded-xl transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <button 
            onClick={() => setMenuOpen(true)} 
            className="p-2 hover:bg-amber-100 rounded-xl transition-colors"
          >
            <Menu className="w-6 h-6 text-gray-700" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Management Signature Names</h1>
            <p className="text-sm text-gray-600">Manage short names for order signatures</p>
          </div>
        </div>

        {/* Add New Name */}
        <div className="bg-white rounded-xl p-4 shadow-md mb-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Add New Name</h2>
          <div className="flex gap-3">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter short name (e.g., SK, MK)"
              className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none"
              onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
            />
            <button
              onClick={handleAdd}
              disabled={isAdding || !newName.trim()}
              className="px-4 py-2 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>
        </div>

        {/* Signature Names List */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-amber-100 to-orange-100 px-4 py-3">
            <h2 className="font-semibold text-gray-800">Current Signature Names</h2>
          </div>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading...</p>
            </div>
          ) : signatureNames.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No signature names found. Add one above.
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {signatureNames.map((sig) => (
                <div key={sig.id} className="flex items-center justify-between px-4 py-3 hover:bg-amber-50">
                  <div>
                    <span className="font-semibold text-gray-800 text-lg">{sig.name}</span>
                    <span className="text-xs text-gray-500 ml-2">({sig.id})</span>
                  </div>
                  <button
                    onClick={() => handleDelete(sig.id)}
                    className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSignatureNames;
